import { gql } from '@apollo/client'

/**
 * Maestro global search — two queries with deliberately different cost profiles.
 *
 * `globalSearchSuggestions` runs on the keystroke path and returns no counts.
 * `globalSearch` backs the results page and runs one search per scope.
 *
 * Both are served by the recruiter schema, i.e. the default Apollo link
 * (`/maestro/api/v1/graphql/recruiter`) — no `clientName` context needed.
 */

const SUGGESTION_FIELDS = `
	term
	hasMore
	suggestions {
		id
		scope
		label
		title
		subtitle
		matchedField
		url
	}
`

export const GLOBAL_SEARCH_SUGGESTIONS_QUERY = gql`
	query globalSearchSuggestions($term: String!, $scope: String, $limit: Int) {
		globalSearchSuggestions(term: $term, scope: $scope, limit: $limit) { ${SUGGESTION_FIELDS} }
	}
`

/** Alias — and matching variable name — for the nth scope of a fan-out. */
export const suggestionAlias = (index) => `scope${index}`

// The document depends only on how many scopes are asked for, so it is parsed
// once per size and reused for every keystroke after that.
const fanOutQueryCache = new Map()

/**
 * The type-ahead fan-out as a single document.
 *
 * The dropdown searches every tab of the page the recruiter is on. That used to
 * be one HTTP request per tab — four on Jobs, six on Candidates, on every
 * debounced keystroke, each paying its own connection, auth header and error
 * link, and all of them racing each other to write the same dropdown.
 *
 * GraphQL aliases ask for exactly the same searches in one request. The engine
 * still runs one Elasticsearch query per scope — that is what a fan-out is — but
 * the browser opens one connection instead of N, and the responses can no longer
 * arrive out of order relative to each other.
 *
 * Per-scope tolerance survives the merge: a scope whose index is down nulls its
 * own alias and, because the client runs `errorPolicy: 'all'`, the scopes that
 * did answer still come back with data.
 *
 * Scopes travel as variables rather than being interpolated into the document,
 * so the cache above can key on the count alone.
 */
export const buildSuggestionsFanOutQuery = (count) => {
	const cached = fanOutQueryCache.get(count)
	if (cached) return cached

	const indexes = Array.from({ length: count }, (_, index) => index)
	const variables = ['$term: String!', '$limit: Int', ...indexes.map((index) => `$${suggestionAlias(index)}: String`)].join(', ')
	const fields = indexes
		.map(
			(index) => `${suggestionAlias(index)}: globalSearchSuggestions(term: $term, scope: $${suggestionAlias(index)}, limit: $limit) { ${SUGGESTION_FIELDS} }`
		)
		.join('\n\t\t\t')

	const query = gql`
		query globalSearchSuggestionsFanOut(${variables}) {
			${fields}
		}
	`

	fanOutQueryCache.set(count, query)
	return query
}

/**
 * `records` is a union, so `__typename` and inline fragments are required. The
 * cache is created with `addTypename: false`, so `__typename` has to be in the
 * document explicitly — it is not injected for us.
 *
 * These are the same `MaestroJob` / `MaestroWorker` types the Jobs and
 * Candidates tables already select from, so the extra fields below are the ones
 * those tables render (see RECRUITER_SEARCH_JOBS_QUERY and WORKER_FRAGMENT).
 * A result row that only carries a title and an unlabelled id is not something
 * a recruiter can act on.
 */
const RECORD_FIELDS = `
	__typename
	... on MaestroJob {
		id
		status
		externalFriendlyId
		jobGeneral {
			title
			address {
				city
				state
			}
			disciplines {
				disciplineName
			}
		}
		facility {
			name
		}
	}
	... on MaestroWorker {
		id
		fullName
		firstName
		lastName
		email
		externalFriendlyId
		engagementScore
		status
		avatar {
			file {
				fileUrl
			}
		}
		workerDisciplines {
			discipline {
				disciplineName
			}
		}
	}
`

// Union members added by gem 0.1.7. Requesting an inline fragment on a type the
// running engine does not know is a validation error that fails the whole
// query, so they live in the full document only.
const EXTENDED_RECORD_FIELDS = `
	... on MaestroJobApplicant {
		id
		status
	}
	... on MaestroWorkerAssignment {
		id
		status
	}
	... on MaestroEmployer {
		id
		fullName
		email
	}
`

/**
 * What the fallback asks for: the field set the API reference itself documents,
 * and nothing more. The enrichment above is read off the sibling table queries
 * rather than the contract, so if any of it is wrong the reduced document is
 * still guaranteed to parse.
 */
const MINIMAL_RECORD_FIELDS = `
	__typename
	... on MaestroJob {
		id
		status
		externalFriendlyId
		jobGeneral {
			title
		}
	}
	... on MaestroWorker {
		id
		fullName
		email
		engagementScore
		status
	}
`

const buildGlobalSearchQuery = ({ extended }) => {
	const recordFields = extended ? `${RECORD_FIELDS}${EXTENDED_RECORD_FIELDS}` : MINIMAL_RECORD_FIELDS
	// `page` and `isApproximate` also arrive with 0.1.7.
	const countFields = extended ? 'scope label page count isApproximate' : 'scope label count'

	return gql`
		query globalSearch($term: String!, $scope: String, $first: Int, $offset: Int) {
			globalSearch(term: $term, scope: $scope, first: $first, offset: $offset) {
				term
				scope
				totalCount
				hasMore
				scopeCounts { ${countFields} }
				groups {
					${countFields}
					records { ${recordFields} }
				}
				records { ${recordFields} }
			}
		}
	`
}

/** Targets gem ≥ 0.1.7. */
export const GLOBAL_SEARCH_QUERY = buildGlobalSearchQuery({ extended: true })

/**
 * Same query minus everything 0.1.7 introduced. The store falls back to this
 * once per session if the full document is rejected, so a tenant on an older
 * engine degrades (no `page` grouping, jobs/candidates records only) instead of
 * losing search altogether.
 */
export const GLOBAL_SEARCH_COMPAT_QUERY = buildGlobalSearchQuery({ extended: false })
